import { Component, OnInit, ElementRef, ViewChild, OnDestroy, inject } from '@angular/core';
import { Notification, GroupedNotification } from '@shared/common/interfaces';
import { trackById } from '@vex/utils/track-by';
import { Router } from '@angular/router';
import { MatRippleModule } from '@angular/material/core';
import { AsyncPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
    LoadMoreNotifications,
    MarkNotificationAsRead,
    DeleteNotification,
    MarkAllAsRead,
    ClearAllNotifications,
} from '../state/notifications.state';
import { Alert } from '@shared/angular/alert';
import { NotificationsState } from '../state/notifications.state';
import { NotificationPreferencesService } from '../services/notification-preferences.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CurrentUserModel } from '@shared/common/models';

@Component({
    selector: 'vex-toolbar-notifications-dropdown',
    templateUrl: './toolbar-notifications-dropdown.component.html',
    styleUrls: ['./toolbar-notifications-dropdown.component.scss'],
    standalone: true,
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        MatRippleModule,
        AsyncPipe,
        MatProgressSpinnerModule,
        MatProgressBarModule,
        MatSnackBarModule,
    ],
})
export class ToolbarNotificationsDropdownComponent implements OnInit, OnDestroy {
    @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef;

    groupedNotifications$: Observable<GroupedNotification[]>;
    isLoading$: Observable<boolean>;
    isLoadingMore$: Observable<boolean>;
    hasNextToken$: Observable<boolean>;

    // Time display toggle
    showExactTime = false;

    // Swipe to delete functionality
    swipedNotificationId: string | null = null;
    private swipeData: {
        notificationId: string;
        startX: number;
        startY: number;
        currentX: number;
        isDragging: boolean;
        threshold: number;
    } = {
        notificationId: '',
        startX: 0,
        startY: 0,
        currentX: 0,
        isDragging: false,
        threshold: 80, // Minimum distance to trigger delete reveal
    };

    trackById = trackById;

    private currentUser = inject(CurrentUserModel);

    constructor(
        private store: Store,
        private preferencesService: NotificationPreferencesService,
        private router: Router,
        private snackBar: MatSnackBar,
        private alert: Alert,
    ) {
        this.groupedNotifications$ = this.store.select(NotificationsState.getGroupedNotifications);
        this.isLoading$ = this.store.select(NotificationsState.isLoading);
        this.isLoadingMore$ = this.store.select(NotificationsState.isLoadingMore);
        this.hasNextToken$ = this.store.select(NotificationsState.hasNextToken);
    }

    ngOnInit(): void {
        // Load saved preference
        this.showExactTime = this.preferencesService.getTimeDisplayPreference();
    }

    onNotificationClick(notification: Notification): void {
        // If notification is swiped, reset the swipe state instead of navigating
        if (this.swipedNotificationId === notification._id) {
            this.resetSwipeState();
            return;
        }

        // Mark as read if not already read
        if (!this.isNotificationRead(notification)) {
            this.store.dispatch(new MarkNotificationAsRead(notification._id)).subscribe({
                error: (error) => {
                    console.error('Failed to mark notification as read:', error);
                    this.snackBar.open('Failed to mark notification as read', 'Close', {
                        duration: 3000,
                        panelClass: 'danger',
                    });
                },
            });
        }

        // Navigate if route is defined in payload
        if (notification.payload?.route) {
            console.log('Navigating to:', notification.payload.route);
            this.navigateToRoute(notification.payload.route);
        }
    }

    private navigateToRoute(route: string): void {
        try {
            // Check if the route contains query parameters
            if (route.includes('?')) {
                const [path, queryString] = route.split('?');
                const queryParams: { [key: string]: string } = {};

                // Parse query parameters
                const params = new URLSearchParams(queryString);
                params.forEach((value, key) => {
                    queryParams[key] = value;
                });

                // Check if we're already on the target route (same path)
                const currentPath = this.router.url.split('?')[0];
                if (currentPath === path) {
                    // If we're already on the same route, use navigateByUrl to force reload
                    // This ensures the queryParams observable emits even when on the same route
                    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                        this.router.navigate([path], { queryParams });
                    });
                } else {
                    // Navigate normally to a different route
                    this.router.navigate([path], { queryParams });
                }
            } else {
                // Simple route without query parameters
                this.router.navigate([route]);
            }
        } catch (error) {
            console.error('Navigation error:', error);
            // Fallback to navigateByUrl for complex routes
            this.router.navigateByUrl(route);
        }
    }

    onScroll(event: Event): void {
        const element = event.target as HTMLElement;
        const threshold = 100;

        if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {
            this.store.dispatch(new LoadMoreNotifications());
        }
    }

    toggleTimeDisplay(): void {
        this.showExactTime = !this.showExactTime;
        this.preferencesService.setTimeDisplayPreference(this.showExactTime);
    }

    getDisplayTime(dateString: string): string {
        return this.showExactTime
            ? this.getExactTime(dateString)
            : this.getRelativeTime(dateString);
    }

    isNotificationRead(notification: Notification): boolean {
        return notification.readBy && notification.readBy.includes(this.currentUser._id);
    }

    hasRoute(notification: Notification): boolean {
        return !!notification.payload?.route;
    }

    getRelativeTime(dateString: string): string {
        const now = new Date();
        const date = new Date(dateString);
        const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours}h ago`;

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d ago`;

        const diffInWeeks = Math.floor(diffInDays / 7);
        return `${diffInWeeks}w ago`;
    }

    getExactTime(dateString: string): string {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const timeOptions: Intl.DateTimeFormatOptions = {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        };

        const timeString = date.toLocaleTimeString('en-US', timeOptions);

        if (date.toDateString() === today.toDateString()) {
            return `Today ${timeString}`;
        }

        if (date.toDateString() === yesterday.toDateString()) {
            return `Yesterday ${timeString}`;
        }

        // For dates older than yesterday, show full date and time
        const dateOptions: Intl.DateTimeFormatOptions = {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
        };

        const formattedDate = date.toLocaleDateString('en-US', dateOptions);
        return `${formattedDate} ${timeString}`;
    }

    formatDate(dateString: string): string {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        }
        if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        }
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    }

    // Swipe to delete functionality
    onSwipeStart(event: MouseEvent | TouchEvent, notificationId: string): void {
        event.preventDefault();

        this.swipeData.notificationId = notificationId;
        this.swipeData.isDragging = true;

        // Handle both mouse and touch events
        if (event instanceof TouchEvent) {
            this.swipeData.startX = event.touches[0].clientX;
            this.swipeData.startY = event.touches[0].clientY;
        } else {
            this.swipeData.startX = event.clientX;
            this.swipeData.startY = event.clientY;
        }

        this.swipeData.currentX = 0;

        // Add global listeners for move and end events
        document.addEventListener('mousemove', this.onSwipeMove.bind(this), { passive: false });
        document.addEventListener('touchmove', this.onSwipeMove.bind(this), { passive: false });
        document.addEventListener('mouseup', this.onSwipeEnd.bind(this));
        document.addEventListener('touchend', this.onSwipeEnd.bind(this));
    }

    onSwipeMove(event: MouseEvent | TouchEvent): void {
        if (!this.swipeData.isDragging) return;

        event.preventDefault();

        let clientX: number;
        if (event instanceof TouchEvent) {
            clientX = event.touches[0].clientX;
        } else {
            clientX = event.clientX;
        }

        const deltaX = clientX - this.swipeData.startX;

        // Only allow left swipe (negative deltaX)
        if (deltaX < 0) {
            this.swipeData.currentX = Math.max(deltaX, -120); // Limit to -120px max
        } else {
            this.swipeData.currentX = 0;
        }
    }

    onSwipeEnd(): void {
        if (!this.swipeData.isDragging) return;

        // Remove global listeners
        document.removeEventListener('mousemove', this.onSwipeMove.bind(this));
        document.removeEventListener('touchmove', this.onSwipeMove.bind(this));
        document.removeEventListener('mouseup', this.onSwipeEnd.bind(this));
        document.removeEventListener('touchend', this.onSwipeEnd.bind(this));

        // Check if swipe distance meets threshold
        if (Math.abs(this.swipeData.currentX) >= this.swipeData.threshold) {
            // Reveal delete button
            this.swipedNotificationId = this.swipeData.notificationId;
        } else {
            // Reset position
            this.swipedNotificationId = null;
            this.swipeData.currentX = 0;
        }

        this.swipeData.isDragging = false;
    }

    getNotificationTransform(notificationId: string): string {
        if (this.swipedNotificationId === notificationId) {
            return 'translateX(-80px)'; // Reveal delete button
        } else if (this.swipeData.isDragging && this.swipeData.notificationId === notificationId) {
            return `translateX(${this.swipeData.currentX}px)`;
        }
        return 'translateX(0)';
    }

    onDeleteNotification(event: Event, notificationId: string): void {
        event.stopPropagation();
        event.preventDefault();

        // Reset swipe state
        this.swipedNotificationId = null;
        this.swipeData.currentX = 0;

        // Dispatch delete action and handle success/error
        this.store.dispatch(new DeleteNotification(notificationId)).subscribe({
            error: (error) => {
                console.error('Failed to delete notification:', error);
                this.snackBar.open('Failed to delete notification. Please try again.', 'Close', {
                    duration: 5000,
                    panelClass: 'danger',
                });
            },
        });
    }

    // Reset swipe state when clicking outside or on notification content
    resetSwipeState(): void {
        this.swipedNotificationId = null;
        this.swipeData.currentX = 0;
    }

    onMarkAllAsRead(): void {
        this.alert
            .confirm({
                title: 'Mark All as Read',
                message: 'Are you sure you want to mark all notifications as read?',
                icon: 'info',
            })
            .subscribe((isConfirmed) => {
                if (isConfirmed) {
                    this.store.dispatch(new MarkAllAsRead()).subscribe({
                        next: () => {
                            this.snackBar.open('All notifications marked as read', 'Close', {
                                duration: 3000,
                                panelClass: 'success',
                            });
                        },
                        error: (error) => {
                            console.error('Failed to mark all notifications as read:', error);
                            this.snackBar.open(
                                'Failed to mark all notifications as read',
                                'Close',
                                {
                                    duration: 5000,
                                    panelClass: 'danger',
                                },
                            );
                        },
                    });
                }
            });
    }

    onClearAllNotifications(): void {
        this.alert
            .confirm({
                title: 'Clear All Notifications',
                message:
                    'Are you sure you want to permanently delete all notifications? This action cannot be undone.',
                icon: 'danger',
            })
            .subscribe((isConfirmed) => {
                if (isConfirmed) {
                    this.store.dispatch(new ClearAllNotifications()).subscribe({
                        next: () => {
                            this.snackBar.open('All notifications cleared successfully', 'Close', {
                                duration: 3000,
                                panelClass: 'success',
                            });
                        },
                        error: (error) => {
                            console.error('Failed to clear all notifications:', error);
                            this.snackBar.open('Failed to clear all notifications', 'Close', {
                                duration: 5000,
                                panelClass: 'danger',
                            });
                        },
                    });
                }
            });
    }

    ngOnDestroy(): void {
        // Clean up any remaining event listeners
        if (this.swipeData.isDragging) {
            document.removeEventListener('mousemove', this.onSwipeMove.bind(this));
            document.removeEventListener('touchmove', this.onSwipeMove.bind(this));
            document.removeEventListener('mouseup', this.onSwipeEnd.bind(this));
            document.removeEventListener('touchend', this.onSwipeEnd.bind(this));
        }
    }
}
